import { formatMoney, formatOfferVersion } from '@devizy/shared';
import { Badge, Button } from '@devizy/ui';
import { PaperPlaneRight } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslations } from 'use-intl';

import { awaitsBidderDecision, bidLifecycleState } from '@/components/bid/bid-lifecycle';
import { EmptyList } from '@/components/dashboard/empty-list';
import { PageHeader } from '@/components/dashboard/page-header';
import { CsvExportButton } from '@/components/export';
import { trpc } from '@/lib/trpc/client';

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  ACTIVE: 'default',
  WAITING: 'secondary',
  WAITING_BIDDER_RESPONSE: 'default',
  WON: 'default',
  OUTDATED: 'destructive',
  EXPIRED: 'outline',
  WITHDRAWN: 'outline',
  WITHDRAWN_BY_REQUESTER: 'outline',
  ARCHIVED: 'outline',
};

const KNOWN_STATUSES = new Set(Object.keys(STATUS_VARIANT));

export const Route = createFileRoute('/_app/dashboard/bidder/bids')({
  component: BidderBidsDashboardPage,
});

function BidderBidsDashboardPage(): React.ReactElement {
  const t = useTranslations('bid.list');
  const { data: bidsData } = trpc.bid.myBids.useQuery({});
  const { data: planData } = trpc.billing.currentPlan.useQuery();
  const bids = bidsData?.items ?? [];
  const isBusiness = planData?.plan === 'BUSINESS';

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <CsvExportButton
            kind="bids"
            disabled={!isBusiness}
            disabledReason={isBusiness ? null : 'BUSINESS_PLAN_REQUIRED'}
          />
        }
      />
      {bids.length === 0 ? (
        <EmptyList
          icon={<PaperPlaneRight className="size-8" weight="regular" />}
          title={t('empty')}
          description={t('exploreFeed')}
          action={{ href: '/dashboard/bidder/feed', label: t('goToFeed') }}
        />
      ) : (
        <ul
          className="divide-border border-border bg-card flex flex-col divide-y rounded-2xl border"
          data-testid="bids-list"
        >
          {bids.map((bid) => {
            const quote = bid.quote;
            const lifecycle = bidLifecycleState(bid.status, bid.selectionStatus);
            const awaitsDecision = awaitsBidderDecision(lifecycle);
            const statusLabel = lifecycle
              ? t(`lifecycle.${lifecycle}.badge`)
              : KNOWN_STATUSES.has(bid.status)
                ? t(`status.${bid.status}`)
                : bid.status;
            return (
              <li
                key={bid.id}
                className="flex flex-wrap items-center justify-between gap-3 p-4"
                data-testid="bid-row"
                data-bid-status={bid.status}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{bid.offer.title}</p>
                  <p className="text-muted-foreground text-xs">
                    {formatOfferVersion(bid.offerVersion, bid.offerVersionMinor)} ·{' '}
                    {bid.submittedAt ? t('statusSubmitted') : t('statusDraft')}
                  </p>
                  {lifecycle ? (
                    <p className="text-primary mt-0.5 text-xs font-medium">
                      {t(`lifecycle.${lifecycle}.hint`)}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant={
                      lifecycle === 'ACCEPTED_PAYMENT_ISSUE'
                        ? 'destructive'
                        : (STATUS_VARIANT[bid.status] ?? 'outline')
                    }
                  >
                    {statusLabel}
                  </Badge>
                  {quote ? (
                    <span className="text-muted-foreground text-xs">
                      {formatMoney(Number(quote.totalHt), quote.currency)}
                    </span>
                  ) : null}
                  {lifecycle ? (
                    <Button
                      asChild
                      size="sm"
                      variant={awaitsDecision ? 'primary' : 'outline'}
                      data-testid="bid-respond-cta"
                    >
                      <Link to="/bids/$bidId/decision" params={{ bidId: bid.id }}>
                        {awaitsDecision ? t('respond') : t('viewDecision')}
                      </Link>
                    </Button>
                  ) : null}
                  <Button asChild size="sm" variant="outline">
                    <Link to="/offers/$id/bid" params={{ id: bid.offer.id }}>
                      {t('open')}
                    </Link>
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
